import { useAtomValue } from 'jotai';
import { CircleAlert, CloudOff, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { useOfflineStorageAvailability } from '@/app-shell/desktop/hooks/useOfflineStorageAvailability';
import { useIsOfflineRecordingEnabled } from '@/app-shell/flags/desktop-flags';
import {
  offlineConnectivityStateAtom,
  offlinePendingCountAtom,
} from '@/atoms/offline-recording';
import { useNavigatorOnline } from '@/hooks/desktop/useNavigatorOnline';
import { cn } from '@/lib/cn';
import type { OfflineConnectivityState } from '@/tauri/offline-recording/offline-recording';
import { isTauri } from '@/tauri/utils';
import { Button } from '@heidi/design-system/button';
import { TypographyP2, typographyVariant } from '@heidi/design-system/typography';

import { useConnectivityRefresh } from './hooks/useConnectivityRefresh';

/**
 * The two states the alert is shown for. `unknown` (no snapshot yet) and
 * `online_ready_to_sync` (healthy) render nothing — the container guards both
 * out before this narrows to `offline` / `backend_unreachable`.
 */
type AlertedConnectivityState = Exclude<
  OfflineConnectivityState,
  'unknown' | 'online_ready_to_sync'
>;

/**
 * What this banner is reporting. The two connectivity states are transient and
 * recoverable; the two `unavailable-*` kinds are neither — the local database
 * was refused at boot, so offline transcribing cannot run for the rest of this
 * run and every later one until the build moves forward.
 *
 * They share a banner deliberately. The connectivity copy promises "you can
 * still transcribe new sessions", which is false once storage is refused, so
 * the two states must not be able to appear side by side saying opposite
 * things — see {@link ConnectivityAlert} for the precedence.
 */
export type OfflineAlertKind =
  | AlertedConnectivityState
  | 'unavailable-outdated-build'
  | 'unavailable-unknown';

/**
 * Passed where the view requires an `onRefresh` it will never call — the
 * unavailable kinds render no action. A shared no-op rather than an inline
 * arrow so the prop is referentially stable across renders.
 */
const NO_REFRESH = () => {};

interface ConnectivityAlertViewProps {
  kind: OfflineAlertKind;
  pendingCount: number;
  isRefreshing: boolean;
  onRefresh: () => void;
}

interface AlertContent {
  icon: ReactNode;
  lead: ReactNode;
  description: ReactNode;
  /**
   * Whether the banner offers a re-probe, and whether it may mention queued
   * work. Both are true only for the connectivity kinds: a refused database has
   * nothing to re-probe and no readable queue to count, so offering either
   * would name work the clinician cannot do and sessions we cannot see.
   */
  isRecoverable: boolean;
}

/**
 * Icon + copy for each alerted state. An exhaustive switch (not a ternary) so
 * adding a new alert kind is a compile error here until it gets its own
 * content. The two connectivity states are "Heidi is unreachable" so they share
 * the `CloudOff` glyph; the bold lead distinguishes them:
 *   - `offline`             → no network at all
 *   - `backend_unreachable` → network is up but Heidi can't be reached
 *
 * The two `unavailable-*` kinds share `CircleAlert` and a common lead, and
 * differ only in what they can honestly say about the cause. Neither offers a
 * retry: there is no self-service recovery for a refused database, and the
 * reset command that would have provided one is not shipped.
 */
const getAlertContent = (kind: OfflineAlertKind): AlertContent => {
  switch (kind) {
    case 'offline':
      return {
        icon: <CloudOff className="size-4.5 shrink-0" aria-hidden />,
        lead: <FormattedMessage defaultMessage="You're offline." id="15mh+anIEH" />,
        description: (
          <FormattedMessage
            defaultMessage="You can still transcribe new sessions and view existing ones. We'll sync everything once you're back online."
            id="Y0ofcsHfJE"
          />
        ),
        isRecoverable: true,
      };
    case 'backend_unreachable':
      return {
        icon: <CloudOff className="size-4.5 shrink-0" aria-hidden />,
        lead: <FormattedMessage defaultMessage="Can't reach Heidi." id="qiAjohUtzW" />,
        description: (
          <FormattedMessage
            defaultMessage="You can still transcribe new sessions and view existing ones. We'll sync everything once the connection's back."
            id="Riq3RS5rLv"
          />
        ),
        isRecoverable: true,
      };
    case 'unavailable-outdated-build':
      return {
        icon: <CircleAlert className="size-4.5 shrink-0" aria-hidden />,
        lead: (
          <FormattedMessage
            defaultMessage="Offline transcribing is unavailable."
            id="TohnIInKQy"
          />
        ),
        description: (
          <FormattedMessage
            defaultMessage="This device's offline data was saved by a newer version of Heidi, so this version can't open it. Nothing already saved here has been changed. Updating Heidi restores offline transcribing."
            id="gxWAge0twq"
          />
        ),
        isRecoverable: false,
      };
    case 'unavailable-unknown':
      return {
        icon: <CircleAlert className="size-4.5 shrink-0" aria-hidden />,
        lead: (
          <FormattedMessage
            defaultMessage="Offline transcribing is unavailable."
            id="TohnIInKQy"
          />
        ),
        description: (
          // No cause and no fix are named, because neither is known. Guessing
          // at one here would send a clinician to do something that cannot help.
          <FormattedMessage
            defaultMessage="Heidi couldn't open its offline storage on this device, so new sessions can't be saved here in this version. Nothing already saved here has been changed."
            id="ADhq+T6oki"
          />
        ),
        isRecoverable: false,
      };
    default: {
      // Exhaustiveness guard — a new OfflineAlertKind must add a branch.
      const exhaustiveCheck: never = kind;
      return exhaustiveCheck;
    }
  }
};

/**
 * Presentational banner — pure props, no atom or Tauri dependency. Exported for
 * Storybook so each visual state can be rendered in isolation; production code
 * should mount {@link ConnectivityAlert} instead.
 *
 * Full-bleed strip — no container, no border, no rounded corners, no card
 * padding — painted on the window's `surface-accent` chrome so it reads as one
 * seamless band with the title bar and the left rail rather than a bordered
 * card sitting on top of them. Mounted inside the content column (see
 * {@link ConnectivityAlert}) so the left rail never clips it.
 *
 * The copy is one run of `p2` text: a bold lead sentence naming the state,
 * then the reassurance in `text-text-secondary`, then the optional pending
 * count. It is a single `<p>` rather than a title/description pair so the two
 * sentences flow on one line at normal window widths.
 *
 * The lead is a `<span>` carrying the `p2Bold` scale rather than
 * `TypographyP2Bold`, because that component renders a block `<p>` and a `<p>`
 * cannot nest inside a `<p>`. `typographyVariant` is the design system's own
 * exported scale, so the size/weight are still design-system-owned.
 *
 * At widths too narrow for one line the text column (`min-w-0 flex-1`) shrinks
 * and the paragraph *wraps* onto a second line. It is deliberately not
 * truncated: the sentence that gets clipped first is the one promising the
 * clinician their sessions are safe, and losing that is worse than a
 * two-line band. `min-w-0` is what stops the text overflowing its column and
 * shoving the Refresh action off the edge instead.
 */
export const ConnectivityAlertView = ({
  kind,
  pendingCount,
  isRefreshing,
  onRefresh,
}: ConnectivityAlertViewProps) => {
  const { icon, lead, description, isRecoverable } = getAlertContent(kind);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-connectivity-alert"
      className="flex w-full shrink-0 items-center gap-4 bg-surface-accent px-4 py-2"
    >
      {/*
        `items-start`, not `items-center`: on one line the two are identical, but
        when the copy wraps a centred icon floats down beside the middle of the
        block instead of staying with the sentence it belongs to.
      */}
      <div className="flex min-w-0 flex-1 items-start gap-2 text-text-primary">
        {icon}
        <TypographyP2 className="text-text-secondary">
          <span
            className={cn(typographyVariant({ variant: 'p2Bold' }), 'text-text-primary')}
          >
            {lead}
          </span>{' '}
          {description}
          {isRecoverable && pendingCount > 0 && (
            <>
              {' '}
              <FormattedMessage
                defaultMessage="{count, plural, one {# session waiting to sync} other {# sessions waiting to sync}}"
                id="BnUfkXljLD"
                values={{ count: pendingCount }}
              />
            </>
          )}
        </TypographyP2>
      </div>
      {isRecoverable && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="shrink-0"
          data-testid="offline-connectivity-refresh"
        >
          <RefreshCw className={cn('size-4', isRefreshing && 'animate-spin')} />
          <FormattedMessage defaultMessage="Refresh" id="rELDbByu8U" />
        </Button>
      )}
    </div>
  );
};

/**
 * Global connectivity alert for offline desktop recording.
 *
 * Mounted once inside `ResizableLayout`, above the panel group, so the strip
 * lives in the content column (already offset past the fixed left rail) and is
 * never clipped by it — while still spanning the full content width. Pure atom
 * read for state — hydrated by `useOfflineConnectivitySync` (mounted in
 * `_app.tsx`) — plus a manual re-probe for the "Refresh" action.
 *
 * Self-contained: gates on the offline-recording flag and `isTauri()` itself, so
 * it can be dropped into the layout without an external wrapper.
 *
 * Renders nothing unless there is a *confirmed* problem, so the app never looks
 * broken while a clinician transcribes offline:
 *   - storage refused        → "offline transcribing is unavailable" copy, at
 *                              highest precedence and regardless of connectivity
 *   - `pending` storage read → falls through to the connectivity states below,
 *                              so a slow read cannot flash a terminal banner
 *   - `unknown`              → `null` (no snapshot yet — stay neutral, no flash)
 *   - `online_ready_to_sync` → `null` (healthy, queue can drain)
 *   - `offline`              → "you're offline" copy
 *   - `backend_unreachable`  → "can't reach Heidi" copy
 * When sessions are queued, the pending count is appended — on the connectivity
 * states only.
 *
 * Desktop-only — self-guards with `isTauri()`.
 */
export const ConnectivityAlert = () => {
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  const connectivity = useAtomValue(offlineConnectivityStateAtom);
  const pendingCount = useAtomValue(offlinePendingCountAtom);
  const storageAvailability = useOfflineStorageAvailability();
  const { isRefreshing, refresh } = useConnectivityRefresh();
  // Subscribed, not sampled — otherwise the banner keeps whatever the WebView
  // said at the last unrelated render and appears or clears late on Windows.
  const browserIsOnline = useNavigatorOnline();

  // Offline recording is a desktop (Tauri) feature behind the umbrella flag —
  // never render on web or when the feature is off.
  if (!isOfflineRecordingEnabled || !isTauri()) return null;

  // Highest precedence, and deliberately not conditional on connectivity. A
  // refused database is terminal for the run, so the connectivity copy below
  // ("you can still transcribe new sessions") is simply false while it holds —
  // the two must never be able to render side by side. Showing it while the
  // clinician is still online is the point: updating is the only thing that
  // resolves it, and mid-consult with no network is the worst time to find out.
  if (storageAvailability.state === 'unavailable') {
    return (
      <ConnectivityAlertView
        kind={
          storageAvailability.reason === 'outdated-build'
            ? 'unavailable-outdated-build'
            : 'unavailable-unknown'
        }
        pendingCount={0}
        isRefreshing={false}
        onRefresh={NO_REFRESH}
      />
    );
  }

  // A confirmed native `online_ready_to_sync` always wins — a WKWebView
  // false-offline signal must not flash the banner over a healthy connection.
  if (connectivity === 'online_ready_to_sync') {
    return null;
  }

  // Resolve which state to surface. Confirmed `offline` / `backend_unreachable`
  // use their own copy. Otherwise (`unknown` — e.g. the probe target was never
  // set because the cached identity is absent or its region is unmapped) fall
  // back to the browser signal: `navigator.onLine === false` means no network at
  // all, so show the "you're offline" banner. This is the same uniform
  // navigator.onLine fail-safe the routing predicate uses, so the banner can't
  // stay silently hidden on an offline relaunch where the atom is stuck
  // `unknown`. A plain `unknown` with the browser online stays neutral (no flash).
  const alertedConnectivity: AlertedConnectivityState | null =
    connectivity === 'offline' || connectivity === 'backend_unreachable'
      ? connectivity
      : browserIsOnline
        ? null
        : 'offline';

  if (!alertedConnectivity) {
    return null;
  }

  return (
    <ConnectivityAlertView
      kind={alertedConnectivity}
      pendingCount={pendingCount}
      isRefreshing={isRefreshing}
      onRefresh={refresh}
    />
  );
};
